import { Alert, Container, Group, Loader, Text, Title } from "@mantine/core";
import { IconAlertCircle } from "@tabler/icons-react";
import { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { accountsAPI, licensesAPI, usersAPI } from "../../services/api";
import { fetchRecentActivity } from "../../store/activity/activitySlice";
import type { AppDispatch, RootState } from "../../store/store";
import RecentActivity from "./RecentActivity";
import StatsGrid, { type DashboardStats } from "./StatsGrid";

const RECENT_ACTIVITY_REFRESH_MS = 30_000;

interface StatsResponse {
	total?: number;
	expiringSoon?: number;
	active?: number;
	offline?: number;
	admins?: number;
	managers?: number;
}

interface APIResponse<T> {
	data: {
		data: {
			accounts?: T;
			licenses?: T;
			users?: T;
			stations?: T;
		};
	};
}

function Dashboard() {
	const [stats, setStats] = useState<DashboardStats | null>(null);
	const [loading, setLoading] = useState(true);
	const [error, setError] = useState<string | null>(null);
	const role = useSelector((state: RootState) => state.auth.user?.role);
	// /users/stats is admin/manager only. Asking for it as a viewer failed the
	// whole Promise.all, so viewers saw nothing but an error.
	const canSeeUsers = role === "admin" || role === "manager";
	// The activity log is admin/manager only too; a viewer would only ever get 403s
	const canSeeActivity = role === "admin" || role === "manager";
	const dispatch = useDispatch<AppDispatch>();
	const recentActivity = useSelector(
		(state: RootState) => state.activity.recent,
	);

	useEffect(() => {
		if (!canSeeActivity) return;
		dispatch(fetchRecentActivity());
		const intervalId = setInterval(
			() => dispatch(fetchRecentActivity()),
			RECENT_ACTIVITY_REFRESH_MS,
		);
		return () => clearInterval(intervalId);
	}, [canSeeActivity, dispatch]);

	useEffect(() => {
		const fetchDashboardData = async () => {
			try {
				setLoading(true);
				setError(null);

				// Fetch data from all APIs in parallel
				const [accountsResponse, licensesResponse, usersResponse] =
					(await Promise.all([
						accountsAPI.getStats(),
						licensesAPI.getStats(),
						canSeeUsers ? usersAPI.getStats() : Promise.resolve(null),
					])) as [
						APIResponse<StatsResponse>,
						APIResponse<StatsResponse>,
						APIResponse<StatsResponse> | null
					];

				// Extract stats from responses with proper type handling
				const accountStats = accountsResponse.data.data as StatsResponse;
				const licenseStatsData = licensesResponse.data.data as {
					overview: StatsResponse;
				};
				const userStats = usersResponse
					? (usersResponse.data.data as StatsResponse)
					: null;

				setStats({
					totalAccounts: accountStats?.total || 0,
					activeAccounts: accountStats?.active || 0,
					activeLicenses: licenseStatsData?.overview?.active || 0,
					// expiringSoon is what the backend sends; reading "expiring"
					// always fell back to 0
					expiringLicenses: licenseStatsData?.overview?.expiringSoon || 0,
					users: userStats && {
						total: userStats.total || 0,
						admins: userStats.admins || 0,
						managers: userStats.managers || 0,
					},
				});
			} catch (err) {
				console.error("Failed to fetch dashboard data:", err);
				setError("Failed to load dashboard data. Please try again.");
			} finally {
				setLoading(false);
			}
		};

		fetchDashboardData();
	}, [canSeeUsers]);

	return (
		<Container size="xl">
			<Group
				justify="space-between"
				mb="lg">
				<div>
					<Title order={2}>Dashboard</Title>
					<Text c="dimmed">Overview of your license management system</Text>
				</div>
			</Group>

			{error && (
				<Alert
					icon={<IconAlertCircle size={16} />}
					title="Error"
					color="red"
					mb="lg">
					{error}
				</Alert>
			)}

			{loading ? (
				<Group
					justify="center"
					py="xl">
					<Loader size="lg" />
				</Group>
			) : stats ? (
				<>
					<StatsGrid stats={stats} />
					{canSeeActivity && (
						<RecentActivity
							entries={recentActivity.entries}
							loading={recentActivity.loading}
							error={recentActivity.error}
						/>
					)}
				</>
			) : null}
		</Container>
	);
}

export default Dashboard;
